import { StoreApi } from 'zustand';

import { DEFAULT_LOCALE, TLocale } from '@northladder/utilities';

/**
 * The supported color palettes/schemes for the app that complements the dark &
 * light modes. This would customize the primary & secondary colors for the apps.
 */
export type TThemeColorScheme =
  | 'amber-scheme'
  | 'green-scheme'
  | 'purple-scheme'
  | 'red-scheme'
  | 'zinc-scheme';

/**
 * The colors to customize the primary color of the site, to contemplate the
 * dark & light mode.
 */
export const COLOR_SCHEMES: TThemeColorScheme[] = [
  'amber-scheme',
  'green-scheme',
  'purple-scheme',
  'red-scheme',
  'zinc-scheme',
];

/**
 * Whether the app should be in dark or light mode, or follow the system default.
 */
export type TThemeMode = 'light' | 'dark' | 'system';

/**
 * -----------------------------------------------------------------------------
 * These types are used for tracking stateful logic for the Auth Session Store
 */
export interface IAppConfigState {
  /**
   * This provides the theme for the app. This is used to determine the
   * theme for the app and the theme for the UI components.
   * Note, this is an override for `isThemeDark`.
   *
   * - On reset, it default to the `purple` which is NorthLadder's default color scheme.
   *
   * ```ts
   * import { useAppConfigStore } from '@northladder/services';
   * const { appColorScheme } = useAppConfigStore();
   * type TThemeColorScheme = 'green-scheme' |'orange-scheme' | 'purple-scheme' | 'red-scheme'
   *  | 'amber-scheme' |'zinc-scheme';
   *
   * ```
   * ---
   * - To use the app `theme` please refer to the `@nl/design-system/useTheme` hook.
   *
   * ```ts
   * import { useTheme } from '@northladder/design-system';
   * const { theme } = useTheme();
   * type TThemeMode = 'dark' |'light' | 'system';
   *
   * ```
   */
  appColorScheme: TThemeColorScheme | null;
  /**
   * This is used to determine if the user has seen the cookie form or not, and
   * the app uses this to prevent showing the form every time between views.
   * TODO: This is a temporary solution and should be removed once we have a
   * proper solution for this is achieved with the bug in setting a cookie for this.
   */
  hasViewedCookieForm: boolean;
  /**
   * Used to determine if the user has seen the onboarding tour or not.
   * NOTE: Currently we have no such feature but this is a documentation of
   * where add such incase it's required.
   */
  hasWatchedOnboarding: boolean;
  /**
   * Used to determine if the sidebar is expanded or not on the app authed
   * dashboard screen.
   */
  isSidebarExpanded: boolean;
  /**
   * Caches the previous local for the user which get's loaded between sessions.
   */
  locale: TLocale;
  /**
   * Persists the fact that someone has viewed the cookie form so it doesn't
   * keep showing up.
   */
  setHasViewedCookieForm: (status: boolean) => void;
  setHasWatchedOnboarding: (status: boolean) => void;
  setIsSidebarExpanded: (status: boolean) => void;
  /**
   * Replaces the current `color palette/scheme` with the new one provided.
   * @param colorScheme The new color palette/scheme to update with.
   */
  setAppColorScheme: (theme: TThemeColorScheme) => void;
  setLocale: (locale: TLocale) => void;
  resetAppConfig: () => void;
}

export type TPersistableAppConfigState = Pick<
  IAppConfigState,
  | 'appColorScheme'
  | 'hasWatchedOnboarding'
  | 'hasViewedCookieForm'
  | 'isSidebarExpanded'
  | 'locale'
>;

/**
 * -----------------------------------------------------------------------------
 * The Default AppConfig state
 */
export const DEFAULT_APP_CONFIG_STATE: TPersistableAppConfigState = {
  appColorScheme: null,
  hasViewedCookieForm: true,
  hasWatchedOnboarding: false,
  isSidebarExpanded: false,
  locale: DEFAULT_LOCALE,
};

export type TSetAppConfigState = StoreApi<IAppConfigState>['setState'];
